import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Save, AlertTriangle, Printer as PrinterIcon } from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import { settingsApi, printingApi, PrinterDto, SettingDto } from '../../services/api';

export const OrdersSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setPageConfig } = usePageConfig();
  
  const [printers, setPrinters] = useState<PrinterDto[]>([]);
  const [currentSetting, setCurrentSetting] = useState<SettingDto | null>(null);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string>('');
  const [selectedPaperSize, setSelectedPaperSize] = useState<string>('');
  const [selectedOrientation, setSelectedOrientation] = useState<string>('');

  // New settings state
  const [selectedInputBin, setSelectedInputBin] = useState<string>('');
  const [selectedDuplexMode, setSelectedDuplexMode] = useState<string>('');
  const [selectedOutputColor, setSelectedOutputColor] = useState<string>('');

  // Impresora de etiquetas de palet (térmica)
  const [selectedLabelPrinterId, setSelectedLabelPrinterId] = useState<string>('');
  const [initialLabelPrinterId, setInitialLabelPrinterId] = useState<string>('');
  const [selectedLabelPaperSize, setSelectedLabelPaperSize] = useState<string>('');
  const [initialLabelPaperSize, setInitialLabelPaperSize] = useState<string>('');
  const [selectedLabelOrientation, setSelectedLabelOrientation] = useState<string>('');
  const [initialLabelOrientation, setInitialLabelOrientation] = useState<string>('');

  // Impresora de la vista de carga (A4)
  const [selectedTruckLoadPrinterId, setSelectedTruckLoadPrinterId] = useState<string>('');
  const [initialTruckLoadPrinterId, setInitialTruckLoadPrinterId] = useState<string>('');
  const [selectedTruckLoadPaperSize, setSelectedTruckLoadPaperSize] = useState<string>('');
  const [initialTruckLoadPaperSize, setInitialTruckLoadPaperSize] = useState<string>('');
  const [selectedTruckLoadOrientation, setSelectedTruckLoadOrientation] = useState<string>('');
  const [initialTruckLoadOrientation, setInitialTruckLoadOrientation] = useState<string>('');

  // Initial values for change detection
  const [initialPrinterId, setInitialPrinterId] = useState<string>('');
  const [initialPaperSize, setInitialPaperSize] = useState<string>('');
  const [initialOrientation, setInitialOrientation] = useState<string>('');

  // Initial values for new settings
  const [initialInputBin, setInitialInputBin] = useState<string>('');
  const [initialDuplexMode, setInitialDuplexMode] = useState<string>('');
  const [initialOutputColor, setInitialOutputColor] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPrinter = printers.find(p => p.id === selectedPrinterId);
  const selectedLabelPrinter = printers.find(p => p.id === selectedLabelPrinterId);
  const selectedTruckLoadPrinter = printers.find(p => p.id === selectedTruckLoadPrinterId);

  useEffect(() => {
    setPageConfig({
      title: 'Ajustes de Comandas',
      description: 'Configuración de impresión y opciones de comandas',
      actions: [
        {
          label: 'Volver',
          icon: ArrowLeft,
          onClick: () => navigate('/comandas'),
          variant: 'secondary',
        },
      ],
    });

    loadData();
  }, [setPageConfig, navigate]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load printers
      const printersData = await printingApi.getPrinters();
      setPrinters(printersData);

      // Load current settings
      const getSafeSetting = async (key: string) => {
        try {
          return await settingsApi.getSetting('WorkOrders', key);
        } catch (e) {
          return null;
        }
      };

      const [
        printerSetting,
        paperSizeSetting,
        orientationSetting,
        inputBinSetting,
        duplexModeSetting,
        outputColorSetting,
        labelPrinterSetting,
        labelPaperSizeSetting,
        labelOrientationSetting,
        truckLoadPrinterSetting,
        truckLoadPaperSizeSetting,
        truckLoadOrientationSetting,
      ] = await Promise.all([
        getSafeSetting('DefaultPrinterId'),
        getSafeSetting('DefaultPaperSize'),
        getSafeSetting('DefaultOrientation'),
        getSafeSetting('DefaultInputBin'),
        getSafeSetting('DefaultDuplexMode'),
        getSafeSetting('DefaultOutputColor'),
        getSafeSetting('PalletLabelPrinterId'),
        getSafeSetting('PalletLabelPaperSize'),
        getSafeSetting('PalletLabelOrientation'),
        getSafeSetting('TruckLoadPrinterId'),
        getSafeSetting('TruckLoadPaperSize'),
        getSafeSetting('TruckLoadOrientation'),
      ]);

      if (printerSetting) {
        setCurrentSetting(printerSetting);
        const pId = printerSetting.value || '';
        setSelectedPrinterId(pId);
        setInitialPrinterId(pId);
      } else {
        setCurrentSetting(null);
        setSelectedPrinterId('');
        setInitialPrinterId('');
      }

      const pSize = paperSizeSetting?.value || '';
      setSelectedPaperSize(pSize);
      setInitialPaperSize(pSize);

      const pOrient = orientationSetting?.value || '';
      setSelectedOrientation(pOrient);
      setInitialOrientation(pOrient);

      const pInputBin = inputBinSetting?.value || '';
      setSelectedInputBin(pInputBin);
      setInitialInputBin(pInputBin);

      const pDuplex = duplexModeSetting?.value || '';
      setSelectedDuplexMode(pDuplex);
      setInitialDuplexMode(pDuplex);

      const pOutputColor = outputColorSetting?.value || '';
      setSelectedOutputColor(pOutputColor);
      setInitialOutputColor(pOutputColor);

      const lbl = labelPrinterSetting?.value || '';
      setSelectedLabelPrinterId(lbl);
      setInitialLabelPrinterId(lbl);

      const lblPaper = labelPaperSizeSetting?.value || '';
      setSelectedLabelPaperSize(lblPaper);
      setInitialLabelPaperSize(lblPaper);

      const lblOrient = labelOrientationSetting?.value || '';
      setSelectedLabelOrientation(lblOrient);
      setInitialLabelOrientation(lblOrient);

      const tl = truckLoadPrinterSetting?.value || '';
      setSelectedTruckLoadPrinterId(tl);
      setInitialTruckLoadPrinterId(tl);

      const tlPaper = truckLoadPaperSizeSetting?.value || '';
      setSelectedTruckLoadPaperSize(tlPaper);
      setInitialTruckLoadPaperSize(tlPaper);

      const tlOrient = truckLoadOrientationSetting?.value || '';
      setSelectedTruckLoadOrientation(tlOrient);
      setInitialTruckLoadOrientation(tlOrient);
    } catch (err: any) {
      console.error('Error loading settings:', err);
      setError(err.message || 'Error al cargar la configuración');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      await settingsApi.updateSetting('WorkOrders', 'DefaultPrinterId', {
        value: selectedPrinterId,
        description: 'Impresora de comandas',
      });

      // Impresora de etiquetas de palet (puede ir vacía si no se usan etiquetas).
      await settingsApi.updateSetting('WorkOrders', 'PalletLabelPrinterId', {
        value: selectedLabelPrinterId,
        description: 'Impresora de etiquetas de palet (térmica 100x150mm)',
      });
      await settingsApi.updateSetting('WorkOrders', 'PalletLabelPaperSize', {
        value: selectedLabelPaperSize,
        description: 'Formato de papel de la impresora de etiquetas',
      });
      await settingsApi.updateSetting('WorkOrders', 'PalletLabelOrientation', {
        value: selectedLabelOrientation,
        description: 'Orientación de la impresora de etiquetas',
      });

      // Impresora de vista de carga (A4).
      await settingsApi.updateSetting('WorkOrders', 'TruckLoadPrinterId', {
        value: selectedTruckLoadPrinterId,
        description: 'Impresora de la vista de carga del camión (A4)',
      });
      await settingsApi.updateSetting('WorkOrders', 'TruckLoadPaperSize', {
        value: selectedTruckLoadPaperSize,
        description: 'Formato de papel de la vista de carga',
      });
      await settingsApi.updateSetting('WorkOrders', 'TruckLoadOrientation', {
        value: selectedTruckLoadOrientation,
        description: 'Orientación de la vista de carga',
      });

      if (selectedPrinterId) {
        if (selectedPaperSize) {
          await settingsApi.updateSetting('WorkOrders', 'DefaultPaperSize', {
            value: selectedPaperSize,
            description: 'Formato de papel por defecto',
          });
        }
        
        if (selectedOrientation) {
          await settingsApi.updateSetting('WorkOrders', 'DefaultOrientation', {
            value: selectedOrientation,
            description: 'Orientación de impresión por defecto',
          });
        }
        
        if (selectedInputBin) {
          await settingsApi.updateSetting('WorkOrders', 'DefaultInputBin', {
            value: selectedInputBin,
            description: 'Bandeja de entrada por defecto',
          });
        }

        if (selectedDuplexMode) {
          await settingsApi.updateSetting('WorkOrders', 'DefaultDuplexMode', {
            value: selectedDuplexMode,
            description: 'Modo doble cara por defecto',
          });
        }

        if (selectedOutputColor) {
          await settingsApi.updateSetting('WorkOrders', 'DefaultOutputColor', {
            value: selectedOutputColor,
            description: 'Modo de color por defecto',
          });
        }
      }

      // Reload to get updated data
      await loadData();
      
      alert('Configuración guardada correctamente');
    } catch (err: any) {
      console.error('Error saving setting:', err);
      setError(err.message || 'Error al guardar la configuración');
    } finally {
      setIsSaving(false);
    }
  };

  const isCurrentPrinterAvailable = () => {
    if (!currentSetting?.value) return true;
    return printers.some(p => p.id === currentSetting.value);
  };

  const isSelectedPrinterAvailable = () => {
    if (!selectedPrinterId) return true;
    return printers.some(p => p.id === selectedPrinterId);
  };

  const hasChanges =
    selectedPrinterId !== initialPrinterId ||
    selectedPaperSize !== initialPaperSize ||
    selectedOrientation !== initialOrientation ||
    selectedInputBin !== initialInputBin ||
    selectedDuplexMode !== initialDuplexMode ||
    selectedOutputColor !== initialOutputColor ||
    selectedLabelPrinterId !== initialLabelPrinterId ||
    selectedLabelPaperSize !== initialLabelPaperSize ||
    selectedLabelOrientation !== initialLabelOrientation ||
    selectedTruckLoadPrinterId !== initialTruckLoadPrinterId ||
    selectedTruckLoadPaperSize !== initialTruckLoadPaperSize ||
    selectedTruckLoadOrientation !== initialTruckLoadOrientation;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-6">
          {error}
        </div>
      )}

      {/* Current printer warning if offline */}
      {currentSetting?.value && !isCurrentPrinterAvailable() && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-amber-900 text-sm">Impresora configurada no disponible</p>
              <p className="text-amber-700 text-sm mt-1">
                La impresora actual <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">{currentSetting.value}</code> no está en línea. 
                Por favor, selecciona otra impresora disponible.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Printer Settings Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <PrinterIcon className="text-green-600" size={24} />
            <div>
              <h2 className="text-lg font-bold text-slate-900">Impresora de comandas</h2>
              <p className="text-sm text-slate-500">Impresora donde se imprimen las comandas automáticamente al crearlas</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Printer selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Impresora
            </label>
            <select
              value={selectedPrinterId}
              onChange={(e) => setSelectedPrinterId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Sin impresora (no imprimir automáticamente)</option>
              {printers.map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printer.name} {printer.location ? `- ${printer.location}` : ''}
                </option>
              ))}
            </select>
            {selectedPrinterId && (
              <p className="text-xs text-slate-500 mt-1">
                Las comandas se imprimirán automáticamente en esta impresora al crearlas
              </p>
            )}
          </div>

          {/* Paper Size and Orientation Settings */}
          {selectedPrinter && selectedPrinter.capabilities && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
              {/* Paper Size */}
              {selectedPrinter.capabilities.paperSizes && selectedPrinter.capabilities.paperSizes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Formato de Papel
                  </label>
                  <select
                    value={selectedPaperSize}
                    onChange={(e) => setSelectedPaperSize(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Automático / Por defecto</option>
                    {selectedPrinter.capabilities.paperSizes.map((size) => (
                      <option key={size.name} value={size.name}>
                        {size.name} ({size.widthMm}x{size.heightMm}mm)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Orientation */}
              {selectedPrinter.capabilities.orientations && selectedPrinter.capabilities.orientations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Orientación
                  </label>
                  <select
                    value={selectedOrientation}
                    onChange={(e) => setSelectedOrientation(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Automático / Por defecto</option>
                    {selectedPrinter.capabilities.orientations.map((orientation) => (
                      <option key={orientation} value={orientation}>
                        {orientation === 'portrait' ? 'Vertical (Portrait)' : 'Horizontal (Landscape)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Input Bin */}
              {selectedPrinter.capabilities.inputBins && selectedPrinter.capabilities.inputBins.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Bandeja de Entrada
                  </label>
                  <select
                    value={selectedInputBin}
                    onChange={(e) => setSelectedInputBin(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Automático / Por defecto</option>
                    {selectedPrinter.capabilities.inputBins.map((bin) => (
                      <option key={bin} value={bin}>
                        {bin}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Duplex Mode */}
              {selectedPrinter.capabilities.duplexModes && selectedPrinter.capabilities.duplexModes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Modo Doble Cara
                  </label>
                  <select
                    value={selectedDuplexMode}
                    onChange={(e) => setSelectedDuplexMode(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Automático / Por defecto</option>
                    {selectedPrinter.capabilities.duplexModes.map((mode) => (
                      <option key={mode} value={mode}>
                        {mode}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Output Color */}
              {selectedPrinter.capabilities.outputColors && selectedPrinter.capabilities.outputColors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Modo de Color
                  </label>
                  <select
                    value={selectedOutputColor}
                    onChange={(e) => setSelectedOutputColor(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Automático / Por defecto</option>
                    {selectedPrinter.capabilities.outputColors.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Info about current setting */}
          {currentSetting && (
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-slate-500">Última actualización:</span>
                  <p className="font-medium text-slate-900">
                    {currentSetting.updatedAt 
                      ? new Date(currentSetting.updatedAt).toLocaleString('es-ES')
                      : 'N/A'}
                  </p>
                </div>
                {currentSetting.updatedBy && (
                  <div>
                    <span className="text-slate-500">Actualizado por:</span>
                    <p className="font-medium text-slate-900">{currentSetting.updatedBy}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button
            onClick={() => navigate('/comandas')}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || (selectedPrinterId && !isSelectedPrinterAvailable())}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save size={18} />
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </div>

      {/* Impresora de etiquetas de palet */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-6">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <PrinterIcon className="text-orange-600" size={24} />
            <div>
              <h2 className="text-lg font-bold text-slate-900">Impresora de etiquetas de palet</h2>
              <p className="text-sm text-slate-500">
                Impresora térmica (100x150 mm) donde se imprimen las etiquetas al cerrar un palet
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Impresora
            </label>
            <select
              value={selectedLabelPrinterId}
              onChange={(e) => setSelectedLabelPrinterId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Sin impresora (no se imprimirán etiquetas)</option>
              {printers.map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printer.name} {printer.location ? `- ${printer.location}` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedLabelPrinter && selectedLabelPrinter.capabilities && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
              {selectedLabelPrinter.capabilities.paperSizes && selectedLabelPrinter.capabilities.paperSizes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Formato de Papel
                  </label>
                  <select
                    value={selectedLabelPaperSize}
                    onChange={(e) => setSelectedLabelPaperSize(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Por defecto de la impresora</option>
                    {selectedLabelPrinter.capabilities.paperSizes.map((size) => (
                      <option key={size.name} value={size.name}>
                        {size.name} ({size.widthMm}x{size.heightMm}mm)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Elige el tamaño que coincida con el rollo de etiquetas (típicamente A6 o 105x148mm).
                  </p>
                </div>
              )}

              {selectedLabelPrinter.capabilities.orientations && selectedLabelPrinter.capabilities.orientations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Orientación
                  </label>
                  <select
                    value={selectedLabelOrientation}
                    onChange={(e) => setSelectedLabelOrientation(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Por defecto (vertical)</option>
                    {selectedLabelPrinter.capabilities.orientations.map((orientation) => (
                      <option key={orientation} value={orientation}>
                        {orientation === 'portrait' ? 'Vertical (Portrait)' : 'Horizontal (Landscape)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Impresora de la vista de carga del camión */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-6">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <PrinterIcon className="text-blue-600" size={24} />
            <div>
              <h2 className="text-lg font-bold text-slate-900">Impresora de vista de carga</h2>
              <p className="text-sm text-slate-500">
                Impresora A4 donde se imprime la hoja con el orden de paradas y la vista del camión para el conductor
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Impresora
            </label>
            <select
              value={selectedTruckLoadPrinterId}
              onChange={(e) => setSelectedTruckLoadPrinterId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Sin impresora (no se imprimirá automáticamente)</option>
              {printers.map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printer.name} {printer.location ? `- ${printer.location}` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedTruckLoadPrinter && selectedTruckLoadPrinter.capabilities && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
              {selectedTruckLoadPrinter.capabilities.paperSizes && selectedTruckLoadPrinter.capabilities.paperSizes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Formato de Papel
                  </label>
                  <select
                    value={selectedTruckLoadPaperSize}
                    onChange={(e) => setSelectedTruckLoadPaperSize(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Por defecto (A4)</option>
                    {selectedTruckLoadPrinter.capabilities.paperSizes.map((size) => (
                      <option key={size.name} value={size.name}>
                        {size.name} ({size.widthMm}x{size.heightMm}mm)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedTruckLoadPrinter.capabilities.orientations && selectedTruckLoadPrinter.capabilities.orientations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Orientación
                  </label>
                  <select
                    value={selectedTruckLoadOrientation}
                    onChange={(e) => setSelectedTruckLoadOrientation(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Por defecto (vertical)</option>
                    {selectedTruckLoadPrinter.capabilities.orientations.map((orientation) => (
                      <option key={orientation} value={orientation}>
                        {orientation === 'portrait' ? 'Vertical (Portrait)' : 'Horizontal (Landscape)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
